import { Card } from '@/components/ui/Card'
import { Tooltip } from '@/components/ui/Tooltip'
import { Info } from 'lucide-react'
import { usePresalesDashboardStore } from '../store'

export function ForecastSection() {
  const { kr1Rolling, kr2Rolling, isLoadingKRs } = usePresalesDashboardStore()

  if (isLoadingKRs || !kr1Rolling || !kr2Rolling) {
    return (
      <div>
        <div className="flex items-center gap-2 mb-3">
          <h2 className="text-sm font-semibold uppercase tracking-wider text-[var(--text-muted)]">
            The Forecast
          </h2>
        </div>
        <div className="space-y-4">
          <Card className="p-6">
            <div className="h-48 bg-[var(--bg-secondary)] animate-pulse rounded" />
          </Card>
          <Card className="p-6">
            <div className="h-48 bg-[var(--bg-secondary)] animate-pulse rounded" />
          </Card>
        </div>
      </div>
    )
  }

  return (
    <div>
      <div className="flex items-center gap-2 mb-3">
        <h2 className="text-sm font-semibold uppercase tracking-wider text-[var(--text-muted)]">
          The Forecast
        </h2>
        <Tooltip
          content={
            <div className="max-w-xs">
              <div className="font-semibold mb-1">Cohort analysis & trends</div>
              <div className="text-xs">
                Group deals by when presales engaged (not when they closed).
                This shows whether new initiatives are improving outcomes over time.
                Early cohorts may have many open deals — maturity badges help contextualize.
              </div>
            </div>
          }
        />
      </div>

      <div className="space-y-4">
        {/* Win Rate Trend + Cohorts */}
        <WinRateForecast kr1Rolling={kr1Rolling} />

        {/* Presales Cycle (new definition: engaged → handover) */}
        <PresalesCycleForecast kr2Rolling={kr2Rolling} />

{/* Deal Lifecycle Reference: Created → Closed Won */}
        {kr2Rolling.create_to_close && kr2Rolling.create_to_close.all_time.deal_count > 0 && (
          <DealLifecycleForecast data={kr2Rolling.create_to_close} />
        )}
      </div>
    </div>
  )
}

// ========== Win Rate Forecast ==========

function WinRateForecast({ kr1Rolling }: { kr1Rolling: any }) {
  const { trend, cohorts, all_time } = kr1Rolling

  const getRatioColor = (ratio: number) => {
    if (ratio === 0) return 'text-[var(--text-muted)]'
    if (ratio >= 60) return 'text-green-400'
    if (ratio >= 50) return 'text-green-300'
    if (ratio >= 40) return 'text-yellow-400'
    return 'text-red-400'
  }

  const getMaturityBadge = (maturity: string) => {
    switch (maturity) {
      case 'Mature':
        return (
          <span className="px-1.5 py-0.5 text-[10px] rounded bg-green-900/30 text-green-300 border border-green-700/40">
            Mature
          </span>
        )
      case 'Maturing':
        return (
          <span className="px-1.5 py-0.5 text-[10px] rounded bg-yellow-900/30 text-yellow-300 border border-yellow-700/40">
            Maturing
          </span>
        )
      default:
        return (
          <span className="px-1.5 py-0.5 text-[10px] rounded bg-blue-900/30 text-blue-300 border border-blue-700/40">
            Early
          </span>
        )
    }
  }

  return (
    <Card className="overflow-hidden">
      <div className="px-5 py-3 border-b border-[var(--border-color)] flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="text-xs font-semibold uppercase tracking-wider text-blue-400">KR1</span>
          <span className="text-sm font-medium text-[var(--text-primary)]">
            Win Rate by Engagement Cohort
          </span>
        </div>
        <span className="text-xs text-[var(--text-muted)]">Target &gt;50%</span>
      </div>

      <div className="p-5 space-y-5">
        {/* Rolling Trend Mini-Chart */}
        <div>
          <h4 className="text-xs font-medium text-[var(--text-muted)] mb-2 uppercase tracking-wider">
            Rolling Win Ratio (6-month window)
          </h4>
          <div className="flex items-end gap-1 h-16">
            {trend.map((point: any, i: number) => {
              const hasData = point.won > 0 || point.lost > 0
              const barHeight = hasData ? Math.max((point.win_ratio / 100) * 100, 4) : 0
              const barColor =
                point.win_ratio >= 60
                  ? 'bg-green-500'
                  : point.win_ratio >= 50
                  ? 'bg-green-400'
                  : point.win_ratio >= 40
                  ? 'bg-yellow-500'
                  : point.win_ratio > 0
                  ? 'bg-red-500'
                  : 'bg-[var(--bg-tertiary)]'

              return (
                <Tooltip
                  key={i}
                  content={
                    <div className="text-xs">
                      <div className="font-semibold">{point.month}</div>
                      <div>Win Ratio: {hasData ? `${point.win_ratio}%` : 'No data'}</div>
                      <div>
                        {point.won}W / {point.lost}L / {point.open}O
                      </div>
                    </div>
                  }
                >
                  <div className="flex-1 flex flex-col items-center gap-0.5">
                    <div className="w-full flex items-end justify-center" style={{ height: '48px' }}>
                      <div
                        className={`w-full max-w-[28px] rounded-t ${barColor} transition-all cursor-help`}
                        style={{ height: `${barHeight}%`, minHeight: hasData ? '3px' : '0' }}
                      />
                    </div>
                    <span className="text-[8px] text-[var(--text-muted)] whitespace-nowrap">
                      {point.month}
                    </span>
                  </div>
                </Tooltip>
              )
            })}
          </div>
          {/* Target line reference */}
          <div className="flex items-center gap-2 mt-0.5">
            <div className="h-px flex-1 border-t border-dashed border-green-600/40" />
            <span className="text-[8px] text-green-600/60">50% target</span>
          </div>
        </div>

        {/* Cohort Table */}
        <div className="overflow-hidden rounded-lg border border-[var(--border-color)]">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-[var(--bg-tertiary)]">
                <th className="text-left px-3 py-2 text-[10px] font-semibold text-[var(--text-secondary)] uppercase tracking-wider">
                  Cohort
                </th>
                <th className="text-center px-2 py-2 text-[10px] font-semibold text-[var(--text-secondary)] uppercase tracking-wider">
                  Engaged
                </th>
                <th className="text-center px-2 py-2 text-[10px] font-semibold text-green-400 uppercase tracking-wider">
                  Won
                </th>
                <th className="text-center px-2 py-2 text-[10px] font-semibold text-red-400 uppercase tracking-wider">
                  Lost
                </th>
                <th className="text-center px-2 py-2 text-[10px] font-semibold text-[var(--text-muted)] uppercase tracking-wider">
                  Open
                </th>
                <th className="text-center px-2 py-2 text-[10px] font-semibold text-[var(--text-secondary)] uppercase tracking-wider">
                  Win %
                </th>
                <th className="text-center px-2 py-2 text-[10px] font-semibold text-[var(--text-secondary)] uppercase tracking-wider">
                  Status
                </th>
                {all_time.by_owner.map((ownerDef: any) => (
                  <th key={ownerDef.owner} className="text-center px-2 py-2 text-[10px] font-semibold text-[var(--text-secondary)] uppercase tracking-wider">
                    {ownerDef.owner}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-[var(--border-color)]">
              {cohorts.map((cohort: any) => {
                const closed = cohort.won + cohort.lost

                return (
                  <tr key={cohort.quarter} className="hover:bg-[var(--bg-secondary)] transition-colors">
                    <td className="px-3 py-2 font-medium text-[var(--text-primary)]">
                      {cohort.quarter}
                    </td>
                    <td className="text-center px-2 py-2 text-[var(--text-secondary)]">
                      {cohort.total_engaged}
                    </td>
                    <td className="text-center px-2 py-2 text-green-400 font-medium">{cohort.won}</td>
                    <td className="text-center px-2 py-2 text-red-400 font-medium">{cohort.lost}</td>
                    <td className="text-center px-2 py-2 text-[var(--text-muted)]">
                      {cohort.still_open}
                    </td>
                    <td className="text-center px-2 py-2">
                      <span className={`font-semibold ${getRatioColor(cohort.win_ratio)}`}>
                        {closed > 0 ? `${cohort.win_ratio}%` : '--'}
                      </span>
                    </td>
                    <td className="text-center px-2 py-2">{getMaturityBadge(cohort.maturity)}</td>
                    {all_time.by_owner.map((ownerDef: any) => {
                      const ownerData = cohort.by_owner.find((o: any) => o.owner === ownerDef.owner)
                      return (
                        <td key={ownerDef.owner} className="text-center px-2 py-2">
                          {ownerData && (ownerData.won + ownerData.lost) > 0 ? (
                            <Tooltip
                              content={
                                <div className="text-xs">
                                  {ownerData.won}W / {ownerData.lost}L / {ownerData.open}O
                                </div>
                              }
                            >
                              <span className={`cursor-help ${getRatioColor(ownerData.win_ratio)}`}>
                                {ownerData.win_ratio.toFixed(0)}%
                              </span>
                            </Tooltip>
                          ) : (
                            <span className="text-[var(--text-muted)]">--</span>
                          )}
                        </td>
                      )
                    })}
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>

        {/* Per Person Summary */}
        <div className="flex items-center gap-4 text-xs text-[var(--text-secondary)]">
          <span className="text-[10px] font-semibold uppercase tracking-wider text-[var(--text-muted)]">
            All Time
          </span>
          {all_time.by_owner.map((owner: any) => {
            const closed = owner.won + owner.lost
            return (
              <span key={owner.owner}>
                <span className="font-medium text-[var(--text-primary)]">{owner.owner}</span>{' '}
                {closed > 0 ? (
                  <span className={getRatioColor(owner.win_ratio)}>
                    {owner.win_ratio.toFixed(0)}% ({owner.won}W/{owner.lost}L)
                  </span>
                ) : (
                  <span className="text-[var(--text-muted)]">No closed</span>
                )}
              </span>
            )
          })}
        </div>
      </div>
    </Card>
  )
}

// ========== Presales Cycle Forecast (New Definition: Engaged → Handover) ==========

function PresalesCycleForecast({ kr2Rolling }: { kr2Rolling: any }) {
  const { trend, cohorts, all_time, baseline_days } = kr2Rolling
  const hasData = all_time.deal_count > 0

  const getDaysColor = (days: number) => {
    if (days === 0 || !hasData) return 'text-[var(--text-muted)]'
    if (days <= baseline_days * 0.85) return 'text-green-400'
    if (days <= baseline_days) return 'text-green-300'
    if (days <= baseline_days * 1.15) return 'text-yellow-400'
    return 'text-red-400'
  }

  const maxTrendDays = Math.max(...trend.map((t: any) => t.avg_days), baseline_days, 1)

  return (
    <Card className="overflow-hidden">
      <div className="px-5 py-3 border-b border-[var(--border-color)] flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="text-xs font-semibold uppercase tracking-wider text-blue-400">KR2</span>
          <span className="text-sm font-medium text-[var(--text-primary)]">
            Presales Cycle by Engagement Cohort
          </span>
          <Tooltip
            content={
              <div className="max-w-xs text-xs">
                <div className="font-semibold mb-1">New definition</div>
                <div>Start: Earlier of SC Assigned or First Demo</div>
                <div>End: SOW Handover Date</div>
                <div className="mt-1 text-gray-400">Only deals with both dates are included.</div>
              </div>
            }
          />
        </div>
        {hasData && (
          <span className="text-xs text-[var(--text-muted)]">Baseline: {baseline_days}d</span>
        )}
      </div>

      <div className="p-5 space-y-5">
        {!hasData ? (
          <div className="py-4 text-center">
            <div className="flex items-center justify-center gap-1.5 mb-2">
              <Info className="h-4 w-4 text-blue-400" />
              <span className="text-sm font-medium text-[var(--text-secondary)]">Awaiting data</span>
            </div>
            <p className="text-xs text-[var(--text-muted)] max-w-sm mx-auto">
              This chart will populate when deals have both an SC assignment/demo event
              and a scope that has reached handover status.
            </p>
          </div>
        ) : (
          <>
            {/* Rolling Trend Mini-Chart */}
            <div>
              <h4 className="text-xs font-medium text-[var(--text-muted)] mb-2 uppercase tracking-wider">
                Rolling Avg Presales Cycle (6-month window)
              </h4>
              <div className="relative">
                <div
                  className="absolute left-0 right-0 border-t border-dashed border-amber-500/40 z-10"
                  style={{ bottom: `${(baseline_days / maxTrendDays) * 48 + 12}px` }}
                >
                  <span className="absolute right-0 -top-3 text-[8px] text-amber-500/60">
                    {baseline_days}d
                  </span>
                </div>

                <div className="flex items-end gap-1 h-16">
                  {trend.map((point: any, i: number) => {
                    const pointHasData = point.deal_count > 0
                    const barHeight = pointHasData ? Math.max((point.avg_days / maxTrendDays) * 100, 4) : 0
                    const barColor = pointHasData
                      ? point.avg_days <= baseline_days * 0.85
                        ? 'bg-green-500'
                        : point.avg_days <= baseline_days
                        ? 'bg-green-400'
                        : point.avg_days <= baseline_days * 1.15
                        ? 'bg-yellow-500'
                        : 'bg-red-500'
                      : 'bg-[var(--bg-tertiary)]'

                    return (
                      <Tooltip
                        key={i}
                        content={
                          <div className="text-xs">
                            <div className="font-semibold">{point.month}</div>
                            {pointHasData ? (
                              <>
                                <div>Avg: {point.avg_days} days</div>
                                <div>Median: {point.median_days} days</div>
                                <div>{point.deal_count} deal{point.deal_count !== 1 ? 's' : ''}</div>
                              </>
                            ) : (
                              <div>No deals</div>
                            )}
                          </div>
                        }
                      >
                        <div className="flex-1 flex flex-col items-center gap-0.5">
                          <div className="w-full flex items-end justify-center" style={{ height: '48px' }}>
                            <div
                              className={`w-full max-w-[28px] rounded-t ${barColor} transition-all cursor-help`}
                              style={{ height: `${barHeight}%`, minHeight: pointHasData ? '3px' : '0' }}
                            />
                          </div>
                          <span className="text-[8px] text-[var(--text-muted)] whitespace-nowrap">
                            {point.month}
                          </span>
                        </div>
                      </Tooltip>
                    )
                  })}
                </div>
              </div>
            </div>

            {/* Cohort Table */}
            <CohortTable cohorts={cohorts} allTime={all_time} getDaysColor={getDaysColor} />

            {/* Per Person Summary */}
            <div className="flex items-center gap-4 text-xs text-[var(--text-secondary)]">
              <span className="text-[10px] font-semibold uppercase tracking-wider text-[var(--text-muted)]">
                All Time
              </span>
              {all_time.by_owner.map((owner: any) => (
                <span key={owner.owner}>
                  <span className="font-medium text-[var(--text-primary)]">{owner.owner}</span>{' '}
                  {owner.deal_count > 0 ? (
                    <span className={getDaysColor(owner.avg_days)}>
                      avg {owner.avg_days}d ({owner.deal_count} deal{owner.deal_count !== 1 ? 's' : ''})
                    </span>
                  ) : (
                    <span className="text-[var(--text-muted)]">No deals</span>
                  )}
                </span>
              ))}
            </div>
          </>
        )}
      </div>
    </Card>
  )
}

// ========== Deal Lifecycle: Created → Closed Won ==========

function DealLifecycleForecast({ data }: { data: any }) {
  const { trend, cohorts, all_time, baseline_days } = data

  const getDaysColor = (days: number) => {
    if (days === 0) return 'text-[var(--text-muted)]'
    if (days <= baseline_days * 0.85) return 'text-green-400'
    if (days <= baseline_days) return 'text-green-300'
    if (days <= baseline_days * 1.15) return 'text-yellow-400'
    return 'text-red-400'
  }

  const maxTrendDays = Math.max(...trend.map((t: any) => t.avg_days), baseline_days, 1)

  return (
    <Card className="overflow-hidden border border-slate-700/30">
      <div className="px-5 py-3 border-b border-slate-700/30 bg-slate-800/20 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="text-xs font-semibold uppercase tracking-wider text-slate-400">Historical Estimate</span>
          <span className="text-sm font-medium text-[var(--text-primary)]">
            Deal Created &rarr; Closed Won
          </span>
          <Tooltip
            content={
              <div className="max-w-xs text-xs">
                <div className="font-semibold mb-1">Deal lifecycle duration</div>
                <div>Start: Deal created date in HubSpot</div>
                <div>End: Deal closed-won date</div>
                <div className="mt-1 text-gray-400">
                  Estimates how long presales deals historically took from creation to close.
                  Only deals with a Solutions Consultant assigned are included.
                </div>
              </div>
            }
          />
        </div>
        <div className="flex items-center gap-3 text-xs text-[var(--text-muted)]">
          <span>Baseline: {baseline_days}d</span>
          <span>&middot;</span>
          <span>{all_time.deal_count} deals</span>
        </div>
      </div>

      <div className="p-5 space-y-5">
        {/* Summary stats row */}
        <div className="flex items-center gap-6 text-sm">
          <div>
            <span className="text-[10px] font-semibold uppercase tracking-wider text-[var(--text-muted)]">All Time</span>
            <div className={`text-xl font-bold ${getDaysColor(all_time.avg_days)}`}>
              {all_time.avg_days}d <span className="text-xs font-normal text-[var(--text-muted)]">avg</span>
            </div>
          </div>
          <div className="text-xs text-[var(--text-muted)] space-y-0.5">
            <div>P50: <span className="text-[var(--text-secondary)]">{all_time.median_days}d</span></div>
            <div>P75: <span className="text-[var(--text-secondary)]">{all_time.p75}d</span></div>
          </div>
        </div>

        {/* Rolling Trend Mini-Chart */}
        <div>
          <h4 className="text-xs font-medium text-[var(--text-muted)] mb-2 uppercase tracking-wider">
            Rolling Avg Deal Duration (6-month window)
          </h4>
          <div className="relative">
            <div
              className="absolute left-0 right-0 border-t border-dashed border-slate-500/40 z-10"
              style={{ bottom: `${(baseline_days / maxTrendDays) * 48 + 12}px` }}
            >
              <span className="absolute right-0 -top-3 text-[8px] text-slate-500/60">
                {baseline_days}d
              </span>
            </div>

            <div className="flex items-end gap-1 h-16">
              {trend.map((point: any, i: number) => {
                const hasData = point.deal_count > 0
                const barHeight = hasData ? Math.max((point.avg_days / maxTrendDays) * 100, 4) : 0
                const barColor = hasData
                  ? point.avg_days <= baseline_days * 0.85
                    ? 'bg-green-500'
                    : point.avg_days <= baseline_days
                    ? 'bg-green-400'
                    : point.avg_days <= baseline_days * 1.15
                    ? 'bg-yellow-500'
                    : 'bg-red-500'
                  : 'bg-[var(--bg-tertiary)]'

                return (
                  <Tooltip
                    key={i}
                    content={
                      <div className="text-xs">
                        <div className="font-semibold">{point.month}</div>
                        {hasData ? (
                          <>
                            <div>Avg: {point.avg_days} days</div>
                            <div>Median: {point.median_days} days</div>
                            <div>{point.deal_count} deal{point.deal_count !== 1 ? 's' : ''}</div>
                          </>
                        ) : (
                          <div>No deals</div>
                        )}
                      </div>
                    }
                  >
                    <div className="flex-1 flex flex-col items-center gap-0.5">
                      <div className="w-full flex items-end justify-center" style={{ height: '48px' }}>
                        <div
                          className={`w-full max-w-[28px] rounded-t ${barColor} transition-all cursor-help`}
                          style={{ height: `${barHeight}%`, minHeight: hasData ? '3px' : '0' }}
                        />
                      </div>
                      <span className="text-[8px] text-[var(--text-muted)] whitespace-nowrap">
                        {point.month}
                      </span>
                    </div>
                  </Tooltip>
                )
              })}
            </div>
          </div>
        </div>

        {/* Cohort Table (no per-owner columns — this is just overall duration) */}
        <CohortTable cohorts={cohorts} allTime={{ ...all_time, by_owner: [] }} getDaysColor={getDaysColor} />
      </div>
    </Card>
  )
}

// ========== Shared Cohort Table ==========

function CohortTable({
  cohorts,
  allTime,
  getDaysColor,
}: {
  cohorts: any[]
  allTime: any
  getDaysColor: (days: number) => string
}) {
  return (
    <div className="overflow-hidden rounded-lg border border-[var(--border-color)]">
      <table className="w-full text-sm">
        <thead>
          <tr className="bg-[var(--bg-tertiary)]">
            <th className="text-left px-3 py-2 text-[10px] font-semibold text-[var(--text-secondary)] uppercase tracking-wider">
              Cohort
            </th>
            <th className="text-center px-2 py-2 text-[10px] font-semibold text-[var(--text-secondary)] uppercase tracking-wider">
              Deals
            </th>
            <th className="text-center px-2 py-2 text-[10px] font-semibold text-[var(--text-secondary)] uppercase tracking-wider">
              Avg Days
            </th>
            <th className="text-center px-2 py-2 text-[10px] font-semibold text-[var(--text-secondary)] uppercase tracking-wider">
              Median
            </th>
            <th className="text-center px-2 py-2 text-[10px] font-semibold text-[var(--text-secondary)] uppercase tracking-wider">
              P75
            </th>
            <th className="text-center px-2 py-2 text-[10px] font-semibold text-[var(--text-secondary)] uppercase tracking-wider">
              P90
            </th>
            {allTime.by_owner.map((ownerDef: any) => (
              <th key={ownerDef.owner} className="text-center px-2 py-2 text-[10px] font-semibold text-[var(--text-secondary)] uppercase tracking-wider">
                {ownerDef.owner}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-[var(--border-color)]">
          {cohorts.map((cohort: any) => (
            <tr key={cohort.quarter} className="hover:bg-[var(--bg-secondary)] transition-colors">
              <td className="px-3 py-2 font-medium text-[var(--text-primary)]">
                {cohort.quarter}
              </td>
              <td className="text-center px-2 py-2 text-[var(--text-secondary)]">
                {cohort.deal_count}
              </td>
              <td className="text-center px-2 py-2">
                <span className={`font-semibold ${getDaysColor(cohort.avg_days)}`}>
                  {cohort.deal_count > 0 ? `${cohort.avg_days}d` : '--'}
                </span>
              </td>
              <td className="text-center px-2 py-2 text-[var(--text-secondary)]">
                {cohort.deal_count > 0 ? `${cohort.median_days}d` : '--'}
              </td>
              <td className="text-center px-2 py-2 text-[var(--text-secondary)]">
                {cohort.deal_count > 0 ? `${cohort.p75}d` : '--'}
              </td>
              <td className="text-center px-2 py-2 text-[var(--text-secondary)]">
                {cohort.deal_count > 0 ? `${cohort.p90}d` : '--'}
              </td>
              {allTime.by_owner.map((ownerDef: any) => {
                const ownerData = cohort.by_owner.find((o: any) => o.owner === ownerDef.owner)
                return (
                  <td key={ownerDef.owner} className="text-center px-2 py-2">
                    {ownerData && ownerData.deal_count > 0 ? (
                      <Tooltip
                        content={
                          <div className="text-xs">{ownerData.deal_count} deal{ownerData.deal_count !== 1 ? 's' : ''}</div>
                        }
                      >
                        <span className={`cursor-help ${getDaysColor(ownerData.avg_days)}`}>
                          {ownerData.avg_days}d
                        </span>
                      </Tooltip>
                    ) : (
                      <span className="text-[var(--text-muted)]">--</span>
                    )}
                  </td>
                )
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}
